import classNames from "classnames";
import moment from "moment";
import React from "react";
import { Dispatch } from "redux";
import { IStore } from "../../../../../redux/store";
import { connect, ConnectedProps } from "react-redux";
import { TAnnex6Record } from "../../../../../redux/app/annex6/@types";
import { TAnnex6GroupD5 } from "../../../../../redux/app/annex6/groups/group-d";
import Annex6OldVersionPreview from "./Annex6OldVersionPreview";
import CommaSeparator from "components/_common/CommaSeparator";
import { commonUtils } from "redux/_common";
import { marpolUtils } from "redux/app/marpol";
import { authSelectors } from "redux/app/auth";
import Annex6TableRows from "../Annex6TableRows";

type OwnProps = {
	annex6Record: TAnnex6Record;
	isStrikedOff?: boolean;
};
type Annex6GroupD5PreviewProps = React.PropsWithChildren<OwnProps>;
function Annex6GroupD5Preview(props: Annex6GroupD5PreviewProps) {
	// @ts-ignore
	const { tenantId } = props;
	const record = props.annex6Record as TAnnex6GroupD5;
	const operationDate = moment(record.selectedDate, "YYYY-MM-DD").format(
		"DD-MMM-YYYY"
	);

	function renderD5Metrics() {
		if (tenantId === "1076c7c10b69e86246f3910b62fc78f5") {
			// render MT on or before August 14, 2025, after M³ for Teekay
			if (moment(record.selectedDate).isSameOrBefore("2025-08-14")) {
				return "MT";
			}
			return "M³";
		} else if (tenantId === "434d9b50-5703-11eb-8d4a-6b516664bdbd") {
			// render MT on or before July 18, 2025, after M³ for EPS
			if (moment(record.selectedDate).isSameOrBefore("2025-07-18")) {
				return "MT";
			}
			return "M³";
		} else {
			return "M³";
		}
	}

	function renderMain() {
		return (
			<Annex6TableRows
				record={record}
				tableRows={[
					[operationDate, "D", 8.2, renderItem8P2()],
					[null, null, 8.3, renderItem8P3()],
					[null, null, "NOTE", record.remarks],
				]}
			/>
		);
	}

	function renderItem8P2(): React.ReactNode {
		if (!record.item8P2.equipmentsUsed || !record.item8P2.changingFrom)
			return <Annex6OldVersionPreview />;

		const dateOfOperationFormatted = moment(
			record.item8P2.date ? record.item8P2.date : record.selectedDate,
			"YYYY-MM-DD"
		).format("DD-MMM-YYYY");

		return (
			<>
				{dateOfOperationFormatted} AT {record.item8P2.time} LT COMPLETED
				CHANGEOVER OF{" "}
				<CommaSeparator
					items={record.item8P2.equipmentsUsed.map((equipment) =>
						equipment.split("_").join(" ")
					)}
					lastItemSeperator={" & "}
				/>{" "}
				FROM {record.item8P2.changingFrom.fuelType} (
				{record.item8P2.changingFrom.sulphurContent}% S) TO{" "}
				{record.item8P2.changingTo.fuelType}(
				{record.item8P2.changingTo.sulphurContent}% S), <br />
				POSITION: {record.item8P2.position.latitude},{" "}
				{record.item8P2.position.longitude}
			</>
		);
	}

	function renderItem8P3(): React.ReactNode {
		if (!record.item8P2.changingFrom || !record.item8P2.changingTo)
			return <Annex6OldVersionPreview />;

		const changingToTanks = record.item8P3.tanks.filter(
			(tank) => tank.selectedFuelType === record.item8P2.changingTo.fuelType
		);
		const changingToQuantityTotal = changingToTanks.reduce(
			(total, tank) => commonUtils.addUp(total, tank.retainedQuantityInMT),
			0
		);

		const changingFromTanks = record.item8P3.tanks.filter(
			(tank) => tank.selectedFuelType === record.item8P2.changingFrom.fuelType
		);
		const changingFromQuantityTotal = changingFromTanks.reduce(
			(total, tank) => commonUtils.addUp(total, tank.retainedQuantityInMT),
			0
		);

		const otherTanks = record.item8P3.tanks.filter(
			(tank) => tank.selectedFuelType === "UNCATEGORIZED"
		);
		const otherTanksQuantityTotal = otherTanks.reduce(
			(total, tank) => commonUtils.addUp(total, tank.retainedQuantityInMT),
			0
		);

		const isChangingFromTanksVisible = changingFromQuantityTotal > 0;
		const isChangingToTanksVisible = changingToQuantityTotal > 0;
		const isUncategorizedTanksVisible = otherTanksQuantityTotal > 0;

		return (
			<>
				{isChangingFromTanksVisible ? (
					<>
						TOTAL {record.item8P2.changingFrom.fuelType} ROB ON COMPLETION OF
						CHANGE OVER {changingFromQuantityTotal}
						{renderD5Metrics()}
						<br />
						<CommaSeparator
							items={changingFromTanks.map(
								(tank) =>
									`${marpolUtils.getFormattedName(tank.selectedTank)} - ${
										tank.retainedQuantityInMT
									}${renderD5Metrics()}`
							)}
							customSeperator={<br />}
						/>
						<br />
					</>
				) : null}
				{isChangingToTanksVisible ? (
					<>
						TOTAL {record.item8P2.changingTo.fuelType} ROB ON COMPLETION OF
						CHANGE OVER {changingToQuantityTotal} {renderD5Metrics()}
						<br />
						<CommaSeparator
							items={changingToTanks.map(
								(tank) =>
									`${marpolUtils.getFormattedName(tank.selectedTank)} - ${
										tank.retainedQuantityInMT
									} ${renderD5Metrics()}`
							)}
							customSeperator={<br />}
						/>
					</>
				) : null}
				{isUncategorizedTanksVisible ? (
					<>
						{otherTanks.length > 0 ? (
							<>
								<br />
								TOTAL UNCATEGORIZED ROB ON COMPLETION OF CHANGE OVER{" "}
								{otherTanksQuantityTotal} {renderD5Metrics()}
								<br />
								<CommaSeparator
									items={otherTanks.map(
										(tank) =>
											`${marpolUtils.getFormattedName(tank.selectedTank)} - ${
												tank.retainedQuantityInMT
											} ${renderD5Metrics()}`
									)}
									customSeperator={<br />}
								/>
							</>
						) : null}
					</>
				) : null}
			</>
		);
	}

	return renderMain();
}

function mapStateToProps(store: IStore) {
	const authStore = store.app.auth;
	const tenantInfo = authSelectors(authStore).getTenantInfo();
	const tenantId = tenantInfo?.id;
	return {
		tenantId,
	};
}
const reduxConnector = connect(mapStateToProps, null);
type PropsFromRedux = ConnectedProps<typeof reduxConnector>;
export default reduxConnector(Annex6GroupD5Preview);
